import { api } from '../api.js';
import { el, openDialog } from '../dom.js';
import { t, formatDate } from '../i18n.js';
import { linkField, LINK_MESSAGES } from './link-field.js';

// Overrides for one date, without touching the series. The series is the
// truth; this is the exception a venue writes when one night differs — a
// guest DJ, a changed door time in the description, a special title.
//
// Empty means "fall back to the series", not "blank". A publisher clearing
// the field expects the series title to come back, not an empty heading.
export async function openOccurrenceForm({ occurrence, onSaved }) {
  const series = await api.get(`/series/${occurrence.seriesId}`);

  const title = el('input', { id: 'of-title', type: 'text', autocomplete: 'off', placeholder: series.title });
  title.value = occurrence.titleOverride ?? '';

  const description = el('textarea', { id: 'of-description', rows: 3, placeholder: series.description ?? '' });
  description.value = occurrence.descriptionOverride ?? '';

  const images = await api.get(`/series/${occurrence.seriesId}/images`);
  const pin = el('select', { id: 'of-image' }, [
    el('option', { value: '' }, t.imageRotates),
    ...images.map((row, index) => el('option', { value: row.id }, `${index + 1}. ${row.alt || t.imageUnnamed}`)),
  ]);
  pin.value = occurrence.imageId ?? '';

  // The date's own links, beside the date's own title and image. A weekly
  // night has a different guest every week, so this is the level an artist
  // link actually belongs on — the series keeps the residency's own page.
  const linkList = linkField({
    initial: occurrence.links ?? [], label: t.fieldLinksDate, idPrefix: 'of-link',
  });

  const error = el('p', { class: 'pub-error', role: 'alert' });
  error.hidden = true;

  const submit = el('button', { class: 'button', type: 'submit' }, t.save);
  const abort = el('button', { class: 'button button-quiet', type: 'button' }, t.cancelAction);

  const form = el('form', { class: 'form form-wide' }, [
    el('h2', { class: 'dialog-title' }, t.editOccurrence),
    el('p', { class: 'card-meta' }, `${formatDate(occurrence.localDate)} · ${series.title}`),
    error,
    field('of-title', t.fieldTitleOverride, title),
    field('of-description', t.fieldDescriptionOverride, description),
    images.length > 0 ? field('of-image', t.fieldPinImage, pin) : null,
    linkList.node,
    el('p', { class: 'card-meta' }, t.overrideHint),
    el('div', { class: 'row' }, [submit, abort]),
  ].filter(Boolean));

  const dialog = openDialog(form);
  title.focus();

  abort.addEventListener('click', () => dialog.close());

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    submit.disabled = true;
    try {
      await api.patch(`/occurrences/${occurrence.id}`, {
        titleOverride: title.value.trim() || null,
        descriptionOverride: description.value.trim() || null,
        imageId: pin.value ? Number(pin.value) : null,
        links: linkList.value(),
      });
      dialog.close();
      await onSaved(t.saved);
    } catch (failure) {
      submit.disabled = false;
      error.textContent = LINK_MESSAGES[failure?.code] ?? t.saveFailed;
      error.hidden = false;
    }
  });
}

function field(id, label, control) {
  return el('div', { class: 'field' }, [el('label', { for: id }, label), control]);
}
